/**
 * Her world — the rooms she keeps, and the wardrobe slots a set asks for.
 *
 * Both are entirely hers to name. The starters below are only starters: throw
 * any of them away, invent your own, call one "her nan's kitchen". Nothing here
 * is an enum, because a predefined list of rooms is a list of somebody else's
 * rooms.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, mediaUrl, type Place, type WardrobeSlot } from '../lib/api';
import BuildWizard from './BuildWizard';

/* Starters, shown until they are used or dismissed. Not stored anywhere. */
const PLACE_STARTERS = [
  { name: 'Her bedroom', kind: 'bedroom' },
  { name: 'The kitchen', kind: 'kitchen' },
  { name: 'Bathroom mirror', kind: 'bathroom' },
  { name: 'The sofa', kind: 'living room' },
];
const SLOT_STARTERS = ['sleepwear', 'activewear', 'going-out', 'loungewear'];

/** A protected image, fetched with the bearer token the media route wants. */
function Img({ src, alt, className }: { src: string | null | undefined; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(src?.startsWith('http') ? src : null);
  useEffect(() => {
    let live = true;
    if (!src) { setUrl(null); return; }
    // A FluidCloud link is just a URL — no token, no fetch, no object URL.
    if (src.startsWith('http')) { setUrl(src); return; }
    void mediaUrl(src).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [src]);
  if (!url) return <span className={className} aria-label={alt} />;
  return <img className={className} src={url} alt={alt} />;
}

interface WorldProps {
  characterId: string;
  characterName: string;
}

export function Places({ characterId }: WorldProps) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [makerFor, setMakerFor] = useState<{ name: string; kind: string } | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);
  /** The room whose wizard is open. Its data comes from `places`, never a copy. */
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ places: Place[] }>(`/characters/${characterId}/places`);
      setPlaces(r.places);
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load her places'); }
  }, [characterId]);

  useEffect(() => { void load(); }, [load]);

  // A room being generated is the only thing here that takes time.
  const building = places.some((p) => p.status === 'running');
  useEffect(() => {
    if (!building) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [building, load]);

  async function create(name: string, kind: string, generate: boolean, describe = '') {
    setBusy(true);
    try {
      const r = await api.post<{ place: Place }>(`/characters/${characterId}/places`,
        { name, kind, description: describe, generate });
      await load();
      setMakerFor(null);
      return r.place;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that room');
      return null;
    } finally { setBusy(false); }
  }

  async function remove(place: Place) {
    if (!window.confirm(`Delete “${place.name}”? Beats that used it will ask for a new room.`)) return;
    try {
      await api.del(`/characters/${characterId}/places/${place.id}`);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not delete that room'); }
  }

  const starters = PLACE_STARTERS.filter((s) =>
    !dismissed.includes(s.name) && !places.some((p) => p.name.toLowerCase() === s.name.toLowerCase()));

  // Photos, build, review, keep — the same four steps she has. The wizard reads
  // the room out of this list, so a poll mid-build reaches it without a copy.
  const open = places.find((p) => p.id === openId);
  if (open) {
    return (
      <BuildWizard
        kind="room"
        collection={`/characters/${characterId}/places`}
        item={{ ...open, note: open.note, extra: { kind: open.kind, description: open.description } }}
        onChanged={load}
        onClose={() => setOpenId(null)}
        closeLabel="Back to her rooms"
      />
    );
  }

  return (
    <div className="card">
      <div className="world-head">
        <div>
          <span className="eyebrow">Her world</span>
          <p className="muted">
            Rooms that stay the same between shots. Name them whatever you like — the ones below are
            only starters, and you can throw any of them away.
          </p>
        </div>
        <span className="pill mono">{places.length} {places.length === 1 ? 'room' : 'rooms'}</span>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="places">
        {places.map((p) => (
          <div key={p.id} className="place">
            <div className="shot">
              <button className="peek" title="Open this room" onClick={() => setOpenId(p.id)}>
                {p.status === 'running'
                  ? <span className="place-building"><span className="spin" />building</span>
                  : p.status === 'failed'
                    ? <span className="place-building bad" title={p.error ?? ''}>failed</span>
                    : <Img src={p.src} alt={p.name} />}
              </button>
              {p.kind && <span className="kind">{p.kind}</span>}
              {/* Kept, waiting to be looked at, or never built — the same three
                  states a reference has, and the reason a beat may not see it. */}
              {p.src && p.status === 'done' && (
                <span className={`state${p.approved ? ' kept' : ' review'}`}>
                  {p.approved ? 'kept' : 'review'}
                </span>
              )}
              <button className="x rm" title="Delete" onClick={() => void remove(p)}>×</button>
            </div>
            <div className="meta">
              <button className="nm link" onClick={() => setOpenId(p.id)}>{p.name}</button>
              {p.usedCount > 0 && <span className="pill">used {p.usedCount}×</span>}
              {!p.ready && p.status === 'done' && (
                <button className="btn btn-sm" onClick={() => setOpenId(p.id)}>Build it</button>
              )}
            </div>
          </div>
        ))}

        <button className="tile newone" onClick={() => { setMakerFor({ name: '', kind: '' }); }}>
          <span style={{ fontSize: 20 }}>+</span><span>New place</span>
          <span className="sub">call it anything</span>
        </button>

        {starters.map((s) => (
          <div key={s.name} className="suggest-wrap">
            <button
              className="x rm" title="Not for this character"
              onClick={() => setDismissed((d) => [...d, s.name])}
            >×</button>
            <button
              className="tile suggested"
              onClick={() => { setMakerFor(s); }}
            >
              <span style={{ fontSize: 17 }}>+</span><span>{s.name}</span>
              <span className="sub">starter</span>
            </button>
          </div>
        ))}
      </div>

      {makerFor && (
        <div className="maker">
          <div className="world-head" style={{ marginBottom: 12 }}>
            <span className="eyebrow" style={{ flex: 1 }}>New place</span>
            <button className="btn btn-sm" onClick={() => { setMakerFor(null); }}>Cancel</button>
          </div>
          <input
            className="field" style={{ maxWidth: 460 }}
            placeholder="What do you call it? — “her nan's kitchen”, “the studio”, “that café”"
            value={makerFor.name}
            onChange={(e) => setMakerFor({ ...makerFor, name: e.target.value })}
          />

          <input
            className="field" style={{ maxWidth: 460, marginTop: 10 }}
            placeholder="What kind of room is it? — “kitchen”, “bedroom”, “that café”"
            value={makerFor.kind}
            onChange={(e) => setMakerFor({ ...makerFor, kind: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || !makerFor.name.trim()}
              onClick={async () => {
                const p = await create(makerFor.name, makerFor.kind, false);
                if (p) setOpenId(p.id);
              }}
            >Add its photos →</button>
            <button className="btn btn-sm" onClick={() => { setMakerFor(null); }}>Cancel</button>
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.5 }}>
            Photos of the room, one build, one look at it — the same four steps her identity set has.
          </p>
        </div>
      )}

      <div className="note" style={{ marginTop: 16 }}>
        <b>A place is a room, not a photo of her in a room.</b> When the anchor carried her face it
        competed with her four references and the likeness drifted. An empty room keeps the variables
        apart: setting from here, identity from the references. One room then works for every outfit,
        hour and beat.
      </div>
    </div>
  );
}

export function Wardrobe({ characterId }: WorldProps) {
  const [slots, setSlots] = useState<WardrobeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  /** The slot whose wizard is open. Its data comes from `slots`, never a copy. */
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ slots: WardrobeSlot[] }>(`/characters/${characterId}/wardrobe`);
      setSlots(r.slots);
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load her wardrobe'); }
  }, [characterId]);

  useEffect(() => { void load(); }, [load]);

  // A garment being built is the only thing here that takes time.
  const building = slots.some((s) => s.status === 'running');
  useEffect(() => {
    if (!building) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [building, load]);

  /** Name the slot, then hand straight to the wizard — as a new room does. */
  async function add(slotName: string) {
    if (!slotName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post<{ slot: WardrobeSlot }>(
        `/characters/${characterId}/wardrobe`, { name: slotName, garment: '' });
      setName(''); setAdding(false);
      await load();
      setOpenId(r.slot.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that slot');
    } finally { setBusy(false); }
  }

  async function remove(slot: WardrobeSlot) {
    if (!window.confirm(`Delete “${slot.name}”? Beats asking for it will show it as unset.`)) return;
    try {
      await api.del(`/characters/${characterId}/wardrobe/${slot.id}`);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not delete that slot'); }
  }

  const starters = SLOT_STARTERS.filter((s) => !slots.some((w) => w.name.toLowerCase() === s));

  // A garment is built the way a room is, and the way she is: photos of it,
  // one build, one look at it, kept.
  const open = slots.find((s) => s.id === openId);
  if (open) {
    return (
      <BuildWizard
        kind="garment"
        collection={`/characters/${characterId}/wardrobe`}
        item={{ ...open, error: open.error ?? null, note: open.note, extra: { garment: open.garment } }}
        onChanged={load}
        onClose={() => setOpenId(null)}
        closeLabel="Back to her wardrobe"
      />
    );
  }

  return (
    <div className="card">
      <div className="world-head">
        <div>
          <span className="eyebrow">Wardrobe</span>
          <p className="muted">
            Named slots, not a garment database. A set says “sleepwear” and she wears whatever you
            bound to it. An outfit persists across beats until one changes the slot — that is the
            whole continuity model.
          </p>
        </div>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 14 }}>{error}</div>}

      {/* The same cards her rooms use. A slot is a thing with a picture, built
          the same way, so it is shown the same way. */}
      <div className="places">
        {slots.map((s) => (
          <div key={s.id} className="place">
            <div className="shot">
              <button className="peek" title="Open this slot" onClick={() => setOpenId(s.id)}>
                {s.status === 'running'
                  ? <span className="place-building"><span className="spin" />building</span>
                  : s.status === 'failed'
                    ? <span className="place-building bad" title={s.error ?? ''}>failed</span>
                    : <Img src={s.src} alt={s.name} />}
              </button>
              {/* A borrowed catalogue swatch is not the same as her own build. */}
              {s.src && !s.own && <span className="kind">swatch</span>}
              {s.src && s.own && s.status === 'done' && (
                <span className={`state${s.approved ? ' kept' : ' review'}`}>
                  {s.approved ? 'kept' : 'review'}
                </span>
              )}
              <button className="x rm" title="Delete" onClick={() => void remove(s)}>×</button>
            </div>
            <div className="meta">
              <button className="nm link" onClick={() => setOpenId(s.id)}>{s.name}</button>
              {!s.src && s.status === 'done' && (
                <button className="btn btn-sm" onClick={() => setOpenId(s.id)}>Build it</button>
              )}
            </div>
            <div className="garment">{s.garment || 'not set'}</div>
          </div>
        ))}

        <button className="tile newone" onClick={() => setAdding(true)}>
          <span style={{ fontSize: 20 }}>+</span><span>New slot</span>
          <span className="sub">call it anything</span>
        </button>

        {starters.map((s) => (
          <button key={s} className="tile suggested" onClick={() => void add(s)}>
            <span style={{ fontSize: 17 }}>+</span><span>{s}</span>
            <span className="sub">starter</span>
          </button>
        ))}
      </div>

      {adding && (
        <div className="maker">
          <div className="world-head" style={{ marginBottom: 12 }}>
            <span className="eyebrow" style={{ flex: 1 }}>New slot</span>
            <button className="btn btn-sm" onClick={() => { setAdding(false); setName(''); }}>Cancel</button>
          </div>
          <input
            className="field" style={{ maxWidth: 460 }} autoFocus
            placeholder="What does a set ask for? — “sleepwear”, “going out”, “work”"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(name); if (e.key === 'Escape') setAdding(false); }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn btn-primary btn-sm" disabled={busy || !name.trim()}
              onClick={() => void add(name)}
            >Add its photos →</button>
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.5 }}>
            Photos of the garment, one build, one look at it — the same four steps a room has.
          </p>
        </div>
      )}

      <div className="note" style={{ marginTop: 16 }}>
        Slot names are yours. Delete <i>work</i> if she does not work; add <i>swim</i>, <i>costume</i>,
        <i>uniform</i>. A set that asks for a slot she has not bound simply shows it as unset, the way
        a missing room does.
      </div>
    </div>
  );
}
